import {takeLatest, put, select} from 'redux-saga/effects';
import {AdditionalHealthPaperwork, AdditionalHealthPaperworkStatus, AddHealth} from 'lib/entities';
import {isConfirmable} from 'lib/useAdditionalHealth';
import * as actions from 'redux/actions/additionalHealthActions';
import {ActionType} from 'redux/actions/additionalHealthActionTypes';
import {AdditionalHealthState} from 'redux/reducers/additionalHealthReducer';
import {HttpError} from 'lib/server/errors';
import {UserState} from '../reducers/usersReducer';
import {AppState} from '../reducers';

type HealthAppState = {
    additionalHealth: AdditionalHealthState;
};

async function saveAdditionalHealthPaperworkAPI(
    paperwork: Partial<AdditionalHealthPaperwork>,
): Promise<AdditionalHealthPaperwork | null> {
    const resp = await fetch('/api/additionalHealth/', {
        method: 'PATCH',
        body: JSON.stringify(paperwork),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    let data;

    try {
        data = await resp.json();
    } catch (e) {
        data = {};
    }

    if (resp.status === 200) return data;

    throw new HttpError(resp.status, data.message || 'Unknown error');
}

// This is a base saga that is used within other sagas and as such should not be
// used as its own due to re-throwing on error.
function* saveAdditionalHealthPaperwork({payload}: Action<Partial<AdditionalHealthPaperwork>>) {
    try {
        const paperwork: AdditionalHealthPaperwork = yield saveAdditionalHealthPaperworkAPI(payload);

        yield put(actions.saveAdditionalHealthPaperworkSuccess(paperwork));
    } catch (e) {
        yield put(actions.saveAdditionalHealthPaperworkFailed(payload, e as Error));
        throw new Error('Could not save additionalHealth paperwork.');
    }
}

function* saveAddHealth({payload, meta}: Action<AddHealth>) {
    try {
        const additionalHealthState: AdditionalHealthState = yield select(
            ({additionalHealth}: HealthAppState) => additionalHealth,
        );

        const userState: UserState = yield select((state: AppState): UserState => state.users[0]);

        const completedPaperwork = {
            ...additionalHealthState.paperwork?.completedPaperwork,
            ...payload,
        };
        yield saveAdditionalHealthPaperwork(
            actions.saveAdditionalHealthPaperwork({
                ...additionalHealthState.paperwork,
                completedPaperwork: JSON.stringify(completedPaperwork),
                status: isConfirmable(completedPaperwork, userState)
                    ? AdditionalHealthPaperworkStatus.CONFIRMED
                    : AdditionalHealthPaperworkStatus.INCOMPLETE,
            }),
        );

        yield put(actions.saveAddHealthSuccess(payload, meta));
    } catch (e) {
        console.error(e);
        yield put(actions.saveAddHealthFailed(payload, e as Error));
    }
}

async function startAdditionalHealthAPI() {
    const resp = await fetch('/api/additionalHealth/', {
        method: 'POST',
    });
    if (resp.status === 200) {
        const paperwork = await resp.json();
        return paperwork;
    }
    return null;
}

function* startAdditionalHealth() {
    try {
        const paperwork: AdditionalHealthPaperwork = yield startAdditionalHealthAPI();
        yield put(actions.startAdditionalHealthSuccess(paperwork));
    } catch (e) {
        yield put(actions.startAdditionalHealthFailed(e as Error));
    }
}

export default function* additionalHealthSaga() {
    yield takeLatest(ActionType.ADDITIONAL_HEALTH_START, startAdditionalHealth);
    yield takeLatest(ActionType.ADDITIONAL_HEALTH_SAVE, saveAddHealth);
}
